import React, { useEffect, useState, useRef } from 'react';
import { FaTrash, FaDownload, FaPlus } from 'react-icons/fa';
import StandardTable from '../components/StandardTable';
import CustomModal from '../components/CustomModal';
import {
    userExcelFileList,
    saveSelectedEntity,
    userSelectedEntityList,
    updateTool,
    fetchEntityTabs,
    deleteUserEntity,
    downloadUserExcel,
    getWorkspaces,
    uploadWordFile,
    updateEntityForm,
    downloadDocFile,
    deleteWordFile,
    fetchTemplateFile,
    excelEntitySearch
} from '../services/api';

import { useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [search, setSearch] = useState('');
    const [templateList, setTemplateList] = useState([]);
    const [wordTemplateList, setWordTemplateList] = useState([]);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [toolOptions, setToolOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingDocId, setDownloadingDocId] = useState(null);
    const [activeDocsEntityId, setActiveDocsEntityId] = useState(null);
    const [downloadingExcelId, setDownloadingExcelId] = useState(null);
    const [error, setError] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchDropdownOptions, setSearchDropdownOptions] = useState([]);
    const searchTimeout = useRef(null);
    const [activeMenuRow, setActiveMenuRow] = useState(null);

    const isAdmin = localStorage.getItem('is_admin') === 'true';

    const addActionsToEntities = (entities) => {
        return entities.map(entity => ({
            ...entity,
            actions: [
                { label: 'Open', onClick: () => handleSheetOpen(entity.id) },
                { label: 'Docs', onClick: () => handleDocsClick(entity.id) },
                ...(isAdmin ? [
                    { label: 'Delete', onClick: () => handleDelete(entity.id) },
                    { label: 'Download', onClick: () => handleExcelDownload(entity.id) }
                ] : [])
            ]
        }));
    };

    const handleNewClick = () => {
        setSearchTerm('');
        setShowModal(true);
    };

    const handleDocsClick = async (entityId) => {
        setSearchTerm('');
        setShowDocsModal(true);
        setActiveDocsEntityId(entityId);
        setLoading(true);
        try {
            const result = await fetchTemplateFile();
            setWordTemplateList(result?.data || []);
        } catch (err) {
            console.error('Error fetching Word templates:', err);
            setError('Failed to load Word templates.');
        } finally {
            setLoading(false);
        }
    };

    const handleSheetOpen = (id) => {
        navigate(`/entity-form/${id}`);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    // Client-side filtering
    const filteredEntities = selectedEntities.filter(entity => {
        if (!search) return true;
        const searchLower = search.toLowerCase();

        // Helper to check values safely
        const checkValue = (val) => {
            if (val === null || val === undefined) return false;
            return String(val).toLowerCase().includes(searchLower);
        };

        // Check columns
        return (
            checkValue(entity.customer_name) ||
            checkValue(entity.address) ||
            checkValue(entity.city) ||
            checkValue(entity.user) ||
            checkValue(new Date(entity.selected_date).toLocaleDateString('nl-NL')) ||
            checkValue(entity.tool)
        );
    });

    const handleDownloadDoc = async (templateId) => {
        try {
            setDownloadingDocId(activeDocsEntityId);
            setShowDocsModal(false);

            const response = await downloadDocFile(activeDocsEntityId, templateId);

            let filename = 'document.docx';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.includes('filename=')) {
                filename = disposition
                    .split('filename=')[1]
                    .replace(/['"]/g, '')
                    .trim();
            }

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Error downloading document:', err);
            setError('Failed to download document. Please try again.');
        } finally {
            setDownloadingDocId(null);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const result = await userExcelFileList();
                setTemplateList(result?.data || []);
                setToolOptions(result?.data || []);
            } catch (err) {
                console.error('Error fetching templates/tool options:', err);
                setError('Failed to load template/tool list.');
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const fetchSelectedEntities = async () => {
        setLoading(true);
        try {
            const result = await userSelectedEntityList();
            setSelectedEntities(addActionsToEntities(result?.data || []));
        } catch (err) {
            console.error('Error fetching selected entity list:', err);
            setError('Failed to load selected entities.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSelectedEntities();
    }, []);

    const filteredList = templateList.filter(item =>
        item.file_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredWordList = wordTemplateList.filter(item =>
        item.file_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleFileClick = async (item) => {
        try {
            await saveSelectedEntity(item.id);
            setShowModal(false);
            const updatedEntities = await userSelectedEntityList();
            setSelectedEntities(addActionsToEntities(updatedEntities?.data || []));
        } catch (err) {
            console.error("Error selecting template:", err);
            setError('Failed to select template. Please try again.');
        }
    };

    const handleToolChange = async (entityId, toolId) => {
        try {
            await updateTool({ id: entityId, tool_id: toolId });
            setSelectedEntities(prev =>
                prev.map(row =>
                    row.id === entityId ? { ...row, tool_id: toolId } : row
                )
            );
        } catch (error) {
            console.error("Failed to update tool:", error);
            setError('Failed to update tool. Please try again.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this entity?')) return;
        try {
            await deleteUserEntity(id);
            fetchSelectedEntities();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleExcelDownload = async (id) => {
        try {
            setDownloadingExcelId(id);
            const response = await downloadUserExcel(id);

            // Default file name
            let filename = 'template.xlsx';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.includes('filename=')) {
                filename = disposition.split('filename=')[1].replace(/['"]/g, '').trim();
            }

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // handle error
            alert("Download failed: " + (err?.message || 'Unknown error'));
        }
        finally {
            setDownloadingExcelId(null);
        }
    };

    const columns = [
        { name: 'Klantnaam', selector: row => row.customer_name || '-', sortable: true, style: { color: '#181F44', fontSize: '18px', fontWeight: 'bold' } },
        { name: 'Date', selector: row => new Date(row.selected_date).toLocaleDateString('nl-NL'), sortable: true, width: '120px' },
        { name: 'Address', selector: row => row.address || '-', sortable: true },
        { name: 'City', selector: row => row.city || '-', sortable: true },
        {
            name: 'Tool',
            cell: row => {
                const selectedToolId = toolOptions.find(opt => opt.file_name === row.tool)?.id || '';
                return (
                    <select
                        className="form-select custom-select"
                        value={selectedToolId}
                        onChange={(e) => handleToolChange(row.id, parseInt(e.target.value))}
                        style={{ minWidth: '140px' }}
                    >
                        <option value="">Select Tool</option>
                        {toolOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                                {opt.file_name}
                            </option>
                        ))}
                    </select>
                );
            },
            sortable: false,
            grow: 1.5,
            wrap: false
        },
        {
            name: 'User', cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <img src={row.user_avatar || '/default-avatar.png'} alt="User Avatar" className="user-avatar" />
                    <span>{row.user}</span>
                </div>
            ), sortable: true, width: '150px'
        },
        {
            name: 'Actions',
            sortable: false,
            width: '60px'
        }
    ];

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h2 className="dashboard-title">Dashboard</h2>
                <button
                    className="btn-new-report"
                    onClick={handleNewClick}
                >
                    Rapport maken
                </button>
            </div>

            <div className="dashboard-cards">
                <div className="dashboard-card">
                    <i className="fas fa-ellipsis-v card-options-icon"></i>
                    <span className="stat-badge">+5</span>
                    <span className="stat-value">12</span>
                    <span className="stat-label">Nieuwe rapporten</span>
                </div>

                <div className="dashboard-card">
                    <i className="fas fa-ellipsis-v card-options-icon"></i>
                    <span className="stat-badge" style={{ backgroundColor: '#E3F8E8', color: '#4CAF50' }}>+2</span>
                    <span className="stat-value">2</span>
                    <span className="stat-label">Openstaande acties</span>
                </div>

                <div className="dashboard-card promo-card">
                    <div className="promo-text">Meld u nu aan <br />voor onze seminar<br />Duurzaam Bouwen.</div>
                    <button className="btn-promo">aanmelden</button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <h2 className="section-title">Overzicht openstaande rapporten</h2>

            <StandardTable
                columns={columns}
                data={filteredEntities}
                search={search}
                onSearchChange={handleSearchChange}
                loading={loading}
                isAdmin={isAdmin}
                activeMenuRow={activeMenuRow}
                setActiveMenuRow={setActiveMenuRow}
            />

            {/* Modal for template selection */}
            <CustomModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title="Selecteer template"
            >
                <input
                    type="text"
                    className="form-control mb-3"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="list-group">
                    {filteredList.length > 0 ? (
                        filteredList.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => handleFileClick(item)}
                            >
                                {item.file_name}
                            </button>
                        ))
                    ) : (
                        <div className="list-group-item text-muted">No results found</div>
                    )}
                </div>
            </CustomModal>

            {/* Modal for document download */}
            {showDocsModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Select Template</h5>
                                <button type="button" className="btn-close" onClick={() => setShowDocsModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="Search templates..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <div className="list-group">
                                    {filteredWordList.length > 0 ? (
                                        filteredWordList.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                onClick={() => handleDownloadDoc(item.id)}
                                                disabled={downloadingDocId === activeDocsEntityId}
                                            >
                                                <span>{item.file_name}</span>
                                                {downloadingDocId === activeDocsEntityId && (
                                                    <div className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                                                )}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="list-group-item text-muted">No results found</div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowDocsModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
